import React, { useRef, useState, useEffect } from "react";
import { useMutation, gql, useLazyQuery, useApolloClient } from "@apollo/client";

import {
  Button,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Switch,
  IconButton,
  Box,
  Autocomplete,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import useStyles from "./styles";
import useGlobalStyles from "../../utils/globalStyles";
import {
  AddMinPurchaseOffer,
  UpdateMinPurchaseOffer,
  getproductsforcs,
  getskuforprodict,
  searchproductforcs,
} from "../../apollo";

const CREATE_MIN_PURCHASE_OFFER = gql`
  ${AddMinPurchaseOffer}
`;

const UPDATE_MIN_PURCHASE_OFFER = gql`
  ${UpdateMinPurchaseOffer}
`;

const SEARCH_PRODUCTS = gql`
  ${searchproductforcs}
`;

const GET_SKUS_FOR_PRODUCT = gql`
  ${getskuforprodict}
`;

const GET_PRODUCTS_FOR_CS = gql`
  ${getproductsforcs}
`;


function MinPurchaseOfferModal({ vendor, onClose, refetch }) {
  const formRef = useRef();
  const client = useApolloClient();

  const classes = useStyles();
  const globalClasses = useGlobalStyles();

  const mutation = vendor
    ? UPDATE_MIN_PURCHASE_OFFER
    : CREATE_MIN_PURCHASE_OFFER;

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [openSnack, setOpenSnack] = useState(false);

  const [errors, setErrors] = useState({});
  const [productSearchText, setProductSearchText] = useState("");
  const [selectedProductsById, setSelectedProductsById] = useState({});
  const [skuOptionsByProductId, setSkuOptionsByProductId] = useState({});
  const inputTextStyle = { color: "black" };
  const userDetails = JSON.parse(localStorage.getItem("user-native")) || {};
  const userStoreId = userDetails.storeId || "";

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    storeId: userStoreId || "",
    minPurchaseAmount: "",
    startDate: "",
    endDate: "",
    isActive: false,
    specialPriceProducts: [
      { productId: "", skuId: "", specialPrice: "", maxQty: "" },
    ],
  });

  const [searchProducts, { data: productData, loading: productsLoading }] =
    useLazyQuery(SEARCH_PRODUCTS, {
      fetchPolicy: "no-cache",
      onError: () => {
        setError("Unable to fetch products");
        setSuccess("");
        setOpenSnack(true);
      },
    });

  const [
    loadInitialProducts,
    { data: initialProductData, loading: initialProductsLoading },
  ] = useLazyQuery(GET_PRODUCTS_FOR_CS, {
    fetchPolicy: "no-cache",
    onError: () => {
      setError("Unable to fetch products");
      setSuccess("");
      setOpenSnack(true);
    },
  });

  const searchedProductOptions =
    (productData &&
      productData.searchProductsCS &&
      productData.searchProductsCS.products) ||
    [];

  const initialProductOptions =
    (initialProductData &&
      initialProductData.getProductsForCS &&
      initialProductData.getProductsForCS.products) ||
    [];

  const productOptions =
    productSearchText.trim().length >= 2
      ? searchedProductOptions
      : initialProductOptions;

  useEffect(() => {
    if (!userStoreId) return;

    loadInitialProducts({
      variables: {
        storeId: userStoreId,
        skip: 0,
        limit: 20,
      },
    });
  }, [loadInitialProducts, userStoreId]);

  useEffect(() => {
    if (!productOptions.length) return;

    setSelectedProductsById((prev) => {
      const next = { ...prev };

      productOptions.forEach((product) => {
        if (product && product._id) {
          next[product._id] = product;
        }
      });

      return next;
    });
  }, [productOptions]);

  useEffect(() => {
    const searchText = productSearchText.trim();

    if (searchText.length < 2) return;

    const timer = setTimeout(() => {
      searchProducts({
        variables: {
          text: searchText,
          skip: 0,
          limit: 20,
        },
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchText, searchProducts]);

  const getProductLabel = (product) => {
    if (!product) return "";

    return product.displayName || product.name || product.productName || "";
  };

  const getSkuLabel = (sku) => {
    if (!sku) return "";

    const skuId = sku._id || sku.skuId || "";
    const unit = [sku.measure, sku.unit].filter(Boolean).join(" ");

    return unit ? `${skuId} - ${unit}` : skuId;
  };

  const getSelectedProduct = (row) => {
    if (!row.productId) return null;

    const fallbackName =
      row.productName && row.productName !== row.skuId
        ? row.productName
        : "";

    return (
      selectedProductsById[row.productId] ||
      productOptions.find((product) => product._id === row.productId) || {
        _id: row.productId,
        name: fallbackName,
        displayName: fallbackName,
        skus: row.skus || [],
      }
    );
  };

  const getSkuOptions = (row) => {
    const selectedProduct = getSelectedProduct(row);
    return (
      skuOptionsByProductId[row.productId] ||
      row.skus ||
      (selectedProduct && selectedProduct.skus) ||
      []
    );
  };

  const getSelectedSku = (row) => {
    if (!row.skuId) return null;

    return (
      getSkuOptions(row).find(
        (sku) => sku._id === row.skuId || sku.skuId === row.skuId
      ) || { _id: row.skuId }
    );
  };

  useEffect(() => {
    if (vendor) {
      let products = [
        { productId: "", skuId: "", specialPrice: "", maxQty: "" },
      ];

      if (
        vendor.specialPriceProducts &&
        vendor.specialPriceProducts.length > 0
      ) {
        products = vendor.specialPriceProducts;
      }

      setFormData({
        id: vendor._id,
        name: vendor.name || "",
        storeId: vendor.storeId || userStoreId || "",
        minPurchaseAmount: vendor.minPurchaseAmount || "",

        startDate: vendor.startDate
          ? vendor.startDate.split("T")[0]
          : "",

        endDate: vendor.endDate
          ? vendor.endDate.split("T")[0]
          : "",

        isActive: Boolean(vendor.isActive),
        specialPriceProducts: products,
      });
    }
  }, [vendor]);

  useEffect(() => {
    const productsToLoad = formData.specialPriceProducts.filter(
      (product) => product.productId && !selectedProductsById[product.productId]
    );

    const storeId = formData.storeId || userStoreId;
    if (!productsToLoad.length || !storeId) return;

    client
      .query({
        query: GET_PRODUCTS_FOR_CS,
        variables: {
          storeId,
          skip: 0,
          limit: 1000,
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const products =
          (data && data.getProductsForCS && data.getProductsForCS.products) || [];

        const productMap = products.reduce((map, product) => {
          if (product && product._id) {
            map[product._id] = product;
          }
          return map;
        }, {});

        setSelectedProductsById((prev) => {
          const next = { ...prev };
          productsToLoad.forEach((product) => {
            const found = productMap[product.productId];
            if (found) {
              next[product.productId] = found;
            }
          });
          return next;
        });

        setSkuOptionsByProductId((prev) => {
          const next = { ...prev };
          productsToLoad.forEach((product) => {
            const found = productMap[product.productId];
            if (found) {
              next[product.productId] = found.skus || [];
            }
          });
          return next;
        });
      })
      .catch(() => {});
  }, [client, formData.specialPriceProducts, selectedProductsById, formData.storeId, userStoreId]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((p) => ({
      ...p,
      [name]: value,
    }));


    if (name === "endDate") {
      if (
        formData.startDate &&
        new Date(value) < new Date(formData.startDate)
      ) {
        setErrors((prev) => ({
          ...prev,
          endDate: "End date must be after Start date",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          endDate: "",
        }));
      }
    }
  };

  const handleProductChange = (index, field, value) => {
    const updated = formData.specialPriceProducts.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );

    setFormData((p) => ({
      ...p,
      specialPriceProducts: updated,
    }));


    setErrors((prev) => ({
      ...prev,
      [`${field}-${index}`]: "",
    }));
  };

  const handleProductSelect = (index, product) => {
    const updated = formData.specialPriceProducts.map((item, i) =>
      i === index
        ? {
            ...item,
            productId: product ? product._id : "",
            productName: product ? product.displayName || product.name : "",
            skus: product ? product.skus || [] : [],
            skuId: "",
          }
        : item
    );

    setFormData((p) => ({
      ...p,
      specialPriceProducts: updated,
    }));

    setErrors((prev) => ({
      ...prev,
      [`productId-${index}`]: "",
      [`skuId-${index}`]: "",
    }));

    if (!product || !product._id) return;

    setSelectedProductsById((prev) => ({
      ...prev,
      [product._id]: product,
    }));

    client
      .query({
        query: GET_SKUS_FOR_PRODUCT,
        variables: {
          productId: product._id,
          storeId: formData.storeId || userStoreId || undefined,
        },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const skus = (data && data.getSkusForProduct) || [];

        setSkuOptionsByProductId((prev) => ({
          ...prev,
          [product._id]: skus,
        }));

        if (skus.length > 0) {
          const firstSkuId = skus[0]._id || skus[0].skuId || "";

          setFormData((prev) => ({
            ...prev,
            specialPriceProducts: prev.specialPriceProducts.map((item, i) =>
              i === index ? { ...item, skuId: firstSkuId } : item
            ),
          }));
        }
      })
      .catch(() => {});
  };


  const addProductRow = () => {
    setFormData((p) => ({
      ...p,
      specialPriceProducts: [
        ...p.specialPriceProducts,
        { productId: "", skuId: "", specialPrice: "", maxQty: "" },
      ],
    }));
  };

  const deleteProductRow = (index) => {
    let updated = formData.specialPriceProducts.filter((_, i) => i !== index);

    if (updated.length === 0) {
      updated = [{ productId: "", skuId: "", specialPrice: "", maxQty: "" }];
    }

    setFormData((p) => ({
      ...p,
      specialPriceProducts: updated,
    }));
  };



  const validateForm = () => {
    let newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Offer name is required";

    if (!formData.minPurchaseAmount || formData.minPurchaseAmount <= 0)
      newErrors.minPurchaseAmount = "Minimum purchase must be > 0";

    if (!formData.startDate) newErrors.startDate = "Start date is required";

    if (!formData.endDate) newErrors.endDate = "End date is required";

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.endDate) < new Date(formData.startDate)
    ) {
      newErrors.endDate = "End date must be after Start date";
    }

    formData.specialPriceProducts.forEach((p, index) => {
      if (!p.productId.trim())
        newErrors[`productId-${index}`] = "Product required";

      if (!p.skuId.trim())
        newErrors[`skuId-${index}`] = "SKU ID required";

      if (!p.specialPrice || p.specialPrice <= 0)
        newErrors[`specialPrice-${index}`] = "Valid price required";

      if (!p.maxQty || p.maxQty <= 0)
        newErrors[`maxQty-${index}`] = "Valid qty required";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const getMutationErrorMessage = (error) => {
    if (!error) return "Something went wrong!";

    const graphQLError =
      error.graphQLErrors && error.graphQLErrors.length > 0
        ? error.graphQLErrors[0]
        : null;

    if (graphQLError && graphQLError.message) {
      return graphQLError.message;
    }

    if (error.message) {
      return error.message;
    }

    return "Something went wrong!";
  };

  const [mutate, { loading }] = useMutation(mutation, {
    onCompleted: () => {
      setSuccess(
        vendor
          ? "Min Purchase Offer Updated Successfully"
          : "Min Purchase Offer Added Successfully"
      );

      setError("");
      setOpenSnack(true);

      if (refetch) refetch();

      setTimeout(() => {
        onClose();
      }, 2000); // 
    },

    onError: (error) => {
      setError(getMutationErrorMessage(error));
      setSuccess("");
      setOpenSnack(true);
    },
  });



  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const offerInput = {
      name: formData.name,
      minPurchaseAmount: Number(formData.minPurchaseAmount),
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString(),
      isActive: formData.isActive,
      specialPriceProducts: formData.specialPriceProducts.map((p) => ({
        productId: p.productId,
        skuId: p.skuId,
        specialPrice: Number(p.specialPrice),
        maxQty: Number(p.maxQty),
      })),
    };


    if (vendor) {
      mutate({
        variables: {
          updateMinPurchaseOfferId: formData.id,
          offerInput,
        },
      });
    }

    if (!vendor) {
      mutate({
        variables: {
          offerInput,
        },
      });
    }
  };



  const isFormValid =
    formData.name.trim() &&
    formData.minPurchaseAmount > 0 &&
    formData.startDate &&
    formData.endDate &&
    new Date(formData.endDate) >= new Date(formData.startDate) &&
    formData.specialPriceProducts.every(
      (p) =>
        p.productId.trim() &&
        p.skuId.trim() &&
        p.specialPrice > 0 &&
        p.maxQty > 0
    );


  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
    >
      <form ref={formRef} onSubmit={handleSubmit}>

        <DialogTitle>
          <Box className={classes.heading}>
            <Typography className={classes.textWhite}>
              {vendor && "Update Min Purchase Offer"}
              {!vendor && "Add Min Purchase Offer"}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Offer Name"
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ style: inputTextStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Purchase Amount"
                name="minPurchaseAmount"
                type="number"
                fullWidth
                value={formData.minPurchaseAmount}
                onChange={handleChange}
                error={!!errors.minPurchaseAmount}
                helperText={errors.minPurchaseAmount}
                inputProps={{ style: inputTextStyle }}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                name="startDate"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formData.startDate}
                onChange={handleChange}
                error={!!errors.startDate}
                helperText={errors.startDate}
                inputProps={{ style: inputTextStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                name="endDate"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={formData.endDate}
                onChange={handleChange}
                error={!!errors.endDate}
                helperText={errors.endDate}
                inputProps={{ style: inputTextStyle }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ color: "black", fontWeight: "bold" }}
              >
                Is Active
              </Typography>
              <Switch
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    isActive: e.target.checked,
                  }))
                }
              />
            </Grid>


            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold"
                sx={{ color: "black", fontWeight: "bold" }}>
                Special Price Products
              </Typography>
            </Grid>


            {formData.specialPriceProducts.map((p, index) => (
              <React.Fragment key={index}>

                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    size="small"
                    options={productOptions}
                    value={getSelectedProduct(p)}
                    loading={productsLoading || initialProductsLoading}
                    filterOptions={(options) => options}
                    ListboxProps={{
                      sx: {
                        color: "black",
                        "& .MuiAutocomplete-option": {
                          color: "black",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiAutocomplete-input": {
                        color: "black",
                      },
                    }}
                    getOptionLabel={getProductLabel}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                    onInputChange={(_, value, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setProductSearchText(value);
                      }
                    }}
                    onChange={(_, value) => handleProductSelect(index, value)}
                    renderInput={({ inputProps, ...params }) => (
                      <TextField
                        {...params}
                        label="Product Name"
                        error={!!errors[`productId-${index}`]}
                        helperText={
                          errors[`productId-${index}`] ||
                          "Search by product name"
                        }
                        inputProps={{
                          ...inputProps,
                          style: inputTextStyle,
                        }}
                      />
                    )}
                  />
                </Grid>


                <Grid item xs={12} sm={5}>
                  <TextField
                    label="SKU ID"
                    fullWidth
                    size="small"
                    value={
                      getSelectedSku(p)
                        ? getSkuLabel(getSelectedSku(p))
                        : p.skuId || ""
                    }
                    InputProps={{
                      readOnly: true,
                      style: inputTextStyle,
                    }}
                    disabled={!p.productId}
                    error={!!errors[`skuId-${index}`]}
                    helperText={
                      errors[`skuId-${index}`] ||
                      "SKU is selected automatically from product"
                    }
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <IconButton
                    color="error"
                    onClick={() => deleteProductRow(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Price"
                    type="number"
                    fullWidth
                    size="small"
                    value={p.specialPrice}
                    onChange={(e) =>
                      handleProductChange(index, "specialPrice", e.target.value)
                    }
                    inputProps={{ style: inputTextStyle }}
                    error={!!errors[`specialPrice-${index}`]}
                    helperText={errors[`specialPrice-${index}`]}
                  />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Max Qty"
                    type="number"
                    fullWidth
                    size="small"
                    value={p.maxQty}
                    onChange={(e) =>
                      handleProductChange(index, "maxQty", e.target.value)
                    }
                    inputProps={{ style: inputTextStyle }}
                    error={!!errors[`maxQty-${index}`]}
                    helperText={errors[`maxQty-${index}`]}
                  />
                </Grid>
              </React.Fragment>
            ))}


            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                onClick={addProductRow}
                sx={{ mt: 1 }}
              >
                + Add Product
              </Button>
            </Grid>
          </Grid>
        </DialogContent>


        <DialogActions>
          <Button
            type="submit"
            className={globalClasses.dashbordBtn}
            disabled={!isFormValid || loading}
          >
            {vendor && "Update"}
            {!vendor && "Add"}
          </Button>

          <Button className={globalClasses.modalCancleBtn} onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </form>


      <Box mt={2}>
        {success && (
          <Snackbar open={openSnack} autoHideDuration={4000}>
            <Alert severity="success" variant="filled">
              {success}
            </Alert>
          </Snackbar>
        )}

        {error && (
          <Snackbar open={openSnack} autoHideDuration={4000}>
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          </Snackbar>
        )}
      </Box>
    </Dialog>
  );
}

export default MinPurchaseOfferModal;
